import React from 'react';
import { useFeatureConfig, FeatureConfig } from '../config/features';

interface FeatureGateProps {
  /** The feature flag key to check */
  feature: keyof FeatureConfig;
  /** Content to render when feature is enabled */
  children: React.ReactNode;
  /** Optional content to render when feature is disabled */
  fallback?: React.ReactNode;
}

/**
 * Conditionally renders children based on feature flags.
 *
 * Usage:
 *   <FeatureGate feature="multiTenant">
 *     <OrganizationsPage />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="licensing" fallback={<SimplifiedView />}>
 *     <LicenseOverview />
 *   </FeatureGate>
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, children, fallback = null }) => {
  const config = useFeatureConfig();
  const isEnabled = Boolean(config[feature]);

  return isEnabled ? <>{children}</> : <>{fallback}</>;
};

/**
 * Inverse of FeatureGate - renders when feature is DISABLED.
 * Useful for on-premise-only features.
 *
 * Usage:
 *   <FeatureGateOff feature="multiTenant">
 *     <SingleOrgDashboard />
 *   </FeatureGateOff>
 */
export const FeatureGateOff: React.FC<FeatureGateProps> = ({ feature, children, fallback = null }) => {
  const config = useFeatureConfig();
  const isEnabled = Boolean(config[feature]);

  return isEnabled ? <>{fallback}</> : <>{children}</>;
};

export default FeatureGate;
